/**
 * US-051 G0 — role-centric grant manifest (the GRANT side of the resource model).
 *
 * Today grants are permission-centric (`Permission.defaults` lists each permission's
 * roles), so a role's full footprint is scattered across the 104-key catalog and is
 * unreviewable for minimum-necessary. This flips authoring to role-centric: one
 * `RoleManifest` per system role declares — EXPLICITLY, read/write flags never
 * collapsed via implication — exactly what that role holds. `Permission.defaults`
 * is generated FROM `ROLE_GRANTS` (inverted); `sync()`, `can()`, and the DB schema
 * are untouched. Ships dark (reproduces US-049's derived defaults byte-identically).
 *
 * Pure module — no server-only deps (same rule as @livingsyncs/types).
 */
import { keyFor, RESOURCE_SCOPED_KEYS } from "@livingsyncs/types";
import type { Resource, ActionKey, PermissionKey } from "@livingsyncs/types";

/** A resource grant with EXPLICIT levels — the literal IS the auditable footprint. */
export interface RoleGrant {
  read: boolean;
  write: boolean;
}

export interface RoleManifest {
  /** Per-resource read/write footprint (typed against the shared `Resource` union). */
  grants: Partial<Record<Resource, RoleGrant>>;
  /** Elevated action gates — explicit, never implied by `write` (§3.2). */
  actions?: ActionKey[];
  /** Required when any granted resource is `phi:true` (§5 / G2 minimum-necessary). */
  justification?: string;
}

const CANONICAL = new Set<string>(RESOURCE_SCOPED_KEYS);

/**
 * Expand a manifest into explicit permission keys. Read-only resources (no `:write`
 * in the catalog) and any non-canonical key are filtered out, so the result is
 * always a subset of the real 104-key catalog.
 */
export function manifestToKeys(manifest: RoleManifest): PermissionKey[] {
  const keys: string[] = [];
  for (const [resource, grant] of Object.entries(manifest.grants) as [
    Resource,
    RoleGrant,
  ][]) {
    if (!grant) continue;
    if (grant.read) keys.push(keyFor(resource, "read"));
    if (grant.write) keys.push(keyFor(resource, "write"));
  }
  for (const action of manifest.actions ?? []) keys.push(action);
  return [...new Set(keys.filter((k) => CANONICAL.has(k)))] as PermissionKey[];
}

export { ROLE_GRANTS } from "./roleGrants.generated";
